import { Flex, Select, Spin, Typography } from "antd";
import { type CSSProperties, useRef, useState } from "react";
import { useSearchUsers } from "@/common/artemis-operations/members";

interface UserSearchSelectProps {
	value?: string;
	onChange?: (userId: string) => void;
	excludeIds?: Set<string>;
	placeholder?: string;
	workspaceId?: string;
	autoFocus?: boolean;
	onBlur?: () => void;
	style?: CSSProperties;
}

/** Debounced user search Select — type to find by name or email, optionally scoped to a workspace. */
export function UserSearchSelect({
	value,
	onChange,
	excludeIds,
	placeholder = "Type to search by name or email",
	workspaceId,
	autoFocus,
	onBlur,
	style,
}: UserSearchSelectProps) {
	const searchUsers = useSearchUsers(workspaceId);
	const [searching, setSearching] = useState(false);
	const [options, setOptions] = useState<
		{ value: string; label: string; email: string }[]
	>([]);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	function handleSearch(query: string) {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (!query.trim()) {
			setOptions([]);
			return;
		}
		debounceRef.current = setTimeout(async () => {
			setSearching(true);
			try {
				const users = await searchUsers(query);
				setOptions(
					users
						.filter((u) => !excludeIds?.has(u.id))
						.map((u) => ({
							value: u.id,
							label: u.displayName ?? u.email ?? u.id,
							email: u.email ?? "",
						})),
				);
			} finally {
				setSearching(false);
			}
		}, 300);
	}

	return (
		<Select
			showSearch
			filterOption={false}
			onSearch={handleSearch}
			onDropdownVisibleChange={(open) => {
				if (!open) setOptions([]);
			}}
			value={value}
			onChange={onChange}
			autoFocus={autoFocus}
			onBlur={onBlur}
			style={style}
			placeholder={placeholder}
			loading={searching}
			notFoundContent={searching ? <Spin size="small" /> : "No users found"}
			optionRender={(option) => (
				<Flex vertical gap={0}>
					<Typography.Text strong>{option.data.label}</Typography.Text>
					<Typography.Text type="secondary" style={{ fontSize: "0.8em" }}>
						{option.data.email}
					</Typography.Text>
				</Flex>
			)}
			options={options}
		/>
	);
}
